import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    BsX, BsSend, BsCurrencyDollar, BsEmojiSmile,
    BsCheckCircle, BsTrash, BsArchive, BsHandThumbsUp, BsChat, BsFileEarmarkText, BsEye, BsClock
} from 'react-icons/bs';
import { useTranslation } from 'react-i18next';
import { useCurrency } from '../../hooks/useCurrency';
import { useCountdown } from '../../hooks/useCountdown';
import { FiAlertCircle } from 'react-icons/fi';
import dispatchService, { DispatchTask, TaskApplication } from '../../services/dispatchService';

interface TaskDetailModalProps {
    taskId: string;
    initialTask?: DispatchTask;
    onClose: () => void;
    onDelete?: (id: string) => void; // If present, implies owner
    onStatusChange?: (id: string, status: string) => void; // If present, implies owner
    onAccept?: (id: string) => void; // Kept for compatibility but unused
    onDecline?: (id: string) => void; // Kept for compatibility but unused
    initialView?: 'details' | 'apply' | 'applications';
}

const TaskDetailModal: React.FC<TaskDetailModalProps> = ({
    taskId,
    initialTask,
    onClose,
    onDelete,
    onStatusChange,
    initialView = 'details'
}) => {
    const { t } = useTranslation();
    const { formatPrice } = useCurrency();
    const [task, setTask] = useState<DispatchTask | null>(initialTask || null);
    const timeLeft = useCountdown(task?.expiresAt);
    const [loading, setLoading] = useState(!initialTask);
    const [view, setView] = useState<'details' | 'apply' | 'applications'>(initialView);

    // Comment State
    const [commentText, setCommentText] = useState('');
    const [submittingComment, setSubmittingComment] = useState(false);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [replyingTo, setReplyingTo] = useState<string | null>(null);
    const [replyText, setReplyText] = useState('');
    const commentsEndRef = useRef<HTMLDivElement>(null);

    // Application State
    const [firstName, setFirstName] = useState('');
    const [middleName, setMiddleName] = useState('');
    const [lastName, setLastName] = useState('');
    const [age, setAge] = useState('');
    const [email, setEmail] = useState('');
    const [country, setCountry] = useState('');
    const [city, setCity] = useState('');
    const [education, setEducation] = useState('');
    const [experience, setExperience] = useState('');
    const [portfolio, setPortfolio] = useState('');
    const [rate, setRate] = useState('');
    const [applicationMessage, setApplicationMessage] = useState('');
    const [cvUrl, setCvUrl] = useState('');
    const [submittingApp, setSubmittingApp] = useState(false);
    const [applications, setApplications] = useState<TaskApplication[]>([]);

    const isOwner = !!onDelete; // Infer ownership from management props
    const quickEmojis = ['👍', '❤️', '🔥', '🎉', '👏', '🚀', '👀', '💯'];
    const isNoCvRequired = task ? ['informal', 'emergency'].includes(task.category || '') : false;

    useEffect(() => {
        loadTaskDetails();
        if (isOwner) {
            loadApplications();
        }
        // Track viewer for urgency metrics
        dispatchService.trackViewer(taskId).catch(err => console.error('Failed to track viewer:', err));
    }, [taskId]);

    useEffect(() => {
        if (task?.comments?.length && view === 'details') {
            commentsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
    }, [task?.comments, view]);

    const loadTaskDetails = async () => {
        try {
            setLoading(true);
            const response = await dispatchService.getTaskById(taskId);
            if (response.success) {
                setTask(response.task);
            }
        } catch (error) {
            console.error('Failed to load task details:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadApplications = async () => {
        try {
            const response = await dispatchService.getTaskApplications(taskId);
            if (response.success) {
                setApplications(response.applications);
            }
        } catch (error) {
            console.error('Failed to load applications:', error);
        }
    };

    const handleSendComment = async () => {
        if (!commentText.trim()) return;
        try {
            setSubmittingComment(true);
            const response = await dispatchService.addComment(taskId, commentText);
            if (response.success) {
                setCommentText('');
                loadTaskDetails(); // Refresh to get correct user/date format or rely on response
            }
        } catch (error) {
            console.error('Failed to add comment:', error);
        } finally {
            setSubmittingComment(false);
        }
    };

    const handleReply = async (commentId: string) => {
        if (!replyText.trim()) return;
        try {
            await dispatchService.replyToComment(taskId, commentId, replyText);
            setReplyText('');
            setReplyingTo(null);
            loadTaskDetails();
        } catch (error) {
            console.error('Failed to reply:', error);
        }
    };

    const handleLike = async (commentId: string) => {
        try {
            await dispatchService.toggleLikeComment(taskId, commentId);
            loadTaskDetails(); // Refresh to update likes count
        } catch (error) {
            console.error('Failed to like comment:', error);
        }
    };

    const handleDeleteComment = async (commentId: string) => {
        if (!window.confirm('Delete this comment?')) return;
        try {
            await dispatchService.deleteComment(taskId, commentId);
            loadTaskDetails();
        } catch (error) {
            console.error('Failed to delete comment:', error);
        }
    };

    const handleApply = async () => {
        // Basic validation
        if (!firstName.trim() || !lastName.trim() || !age.trim() || !email.trim() || !applicationMessage.trim()) {
            alert('Please fill in all required fields (marked with *).');
            return;
        }

        try {
            setSubmittingApp(true);

            let fullMessage = `Applicant Profile:
------------------
Name: ${firstName.trim()} ${middleName.trim()} ${lastName.trim()}
Age: ${age.trim()}
Email: ${email.trim()}
Location: ${city.trim()}, ${country.trim()}

Application Message:
--------------------
${applicationMessage}`;

            // Add professional details if provided or if not strictly no-cv
            if (!isNoCvRequired || (education || experience || rate || portfolio)) {
                fullMessage += `

Professional Details:
---------------------
Education: ${education.trim() || 'N/A'}
Experience: ${experience.trim() || 'N/A'}
Expected Rate: ${rate.trim() || 'N/A'}
Portfolio / Personal Website: ${portfolio.trim() || 'N/A'}`;
            }

            await dispatchService.applyForTask(taskId, fullMessage, cvUrl || portfolio);
            alert('Application submitted successfully!');
            setView('details');
            loadTaskDetails();

            // Reset form
            setFirstName('');
            setMiddleName('');
            setLastName('');
            setAge('');
            setEmail('');
            setCountry('');
            setCity('');
            setEducation('');
            setExperience('');
            setPortfolio('');
            setRate('');
            setApplicationMessage('');
            setCvUrl('');
        } catch (error: any) {
            console.error('Failed to apply:', error);
            alert(error.response?.data?.error || 'Failed to submit application');
        } finally {
            setSubmittingApp(false);
        }
    };

    const handleAdjudicate = async (appId: string, status: 'accepted' | 'rejected') => {
        try {
            await dispatchService.adjudicateApplication(taskId, appId, status);
            loadApplications();
            loadTaskDetails();
        } catch (error) {
            console.error('Failed to update application:', error);
        }
    };

    const handleToggleCommenting = async () => {
        try {
            const response = await dispatchService.toggleCommenting(taskId, !task?.commentingEnabled);
            if (response.success) {
                setTask(prev => prev ? { ...prev, commentingEnabled: !prev.commentingEnabled } : null);
            }
        } catch (error) {
            console.error('Failed to toggle commenting:', error);
        }
    };

    const handleEmojiClick = (emoji: string) => {
        setCommentText(prev => prev + emoji);
    };

    const getStatusBadge = (status: string) => {
        const statusConfig: Record<string, { label: string; color: string }> = {
            pending: { label: 'Pending', color: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/50' },
            dispatched: { label: 'Dispatched', color: 'bg-blue-500/20 text-blue-300 border-blue-500/50' },
            assigned: { label: 'Assigned', color: 'bg-green-500/20 text-green-300 border-green-500/50' },
            declined: { label: 'Declined', color: 'bg-red-500/20 text-red-300 border-red-500/50' },
            expired: { label: 'Expired', color: 'bg-gray-500/20 text-gray-300 border-gray-500/50' }
        };
        const config = statusConfig[status] || statusConfig.pending;
        return (
            <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${config.color}`}>
                {config.label}
            </span>
        );
    };

    if (loading && !task) {
        return (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
                <div className="animate-spin w-12 h-12 border-4 border-premium-gold border-t-transparent rounded-full"></div>
            </div>
        );
    }

    if (!task) return null;

    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={onClose}
                    className="absolute inset-0 bg-black/80 backdrop-blur-md"
                />

                <motion.div
                    initial={{ scale: 0.95, opacity: 0, y: 20 }}
                    animate={{ scale: 1, opacity: 1, y: 0 }}
                    exit={{ scale: 0.95, opacity: 0, y: 20 }}
                    className="relative w-full max-w-6xl max-h-[90vh] bg-premium-wood-dark border border-premium-gold/30 rounded-fluid-2xl shadow-[0_0_50px_rgba(0,0,0,0.5)] overflow-hidden flex flex-col md:flex-row"
                >
                    <button
                        onClick={onClose}
                        className="absolute top-4 right-4 z-10 p-2 text-white/60 hover:text-white bg-black/20 hover:bg-black/40 rounded-full transition-colors"
                    >
                        <BsX className="w-6 h-6" />
                    </button>

                    {/* Left Panel: Main Content */}
                    <div className={`p-6 md:p-8 overflow-y-auto border-b md:border-b-0 md:border-r border-premium-gold/20 custom-scrollbar transition-all duration-300 ${view === 'apply' ? 'w-full' : 'w-full md:w-3/5'}`}>
                        {view === 'apply' ? (
                            <div className="space-y-6">
                                <h2 className="text-2xl font-serif font-bold text-premium-gold mb-4">Apply for {task.title}</h2>

                                {/* Personal Details */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-white/70 mb-2">First Name *</label>
                                        <input
                                            type="text"
                                            value={firstName}
                                            onChange={(e) => setFirstName(e.target.value)}
                                            className="w-full bg-white/5 border border-white/10 rounded-xl p-3 text-white focus:border-premium-gold/50 outline-none"
                                            placeholder="John"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-white/70 mb-2">Middle Name (Optional)</label>
                                        <input
                                            type="text"
                                            value={middleName}
                                            onChange={(e) => setMiddleName(e.target.value)}
                                            className="w-full bg-white/5 border border-white/10 rounded-xl p-3 text-white focus:border-premium-gold/50 outline-none"
                                            placeholder="Middle"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-white/70 mb-2">Surname *</label>
                                        <input
                                            type="text"
                                            value={lastName}
                                            onChange={(e) => setLastName(e.target.value)}
                                            className="w-full bg-white/5 border border-white/10 rounded-xl p-3 text-white focus:border-premium-gold/50 outline-none"
                                            placeholder="Doe"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-white/70 mb-2">Age *</label>
                                        <input
                                            type="number"
                                            value={age}
                                            onChange={(e) => setAge(e.target.value)}
                                            className="w-full bg-white/5 border border-white/10 rounded-xl p-3 text-white focus:border-premium-gold/50 outline-none"
                                            placeholder="25"
                                        />
                                    </div>
                                    <div className="md:col-span-2">
                                        <label className="block text-white/70 mb-2">Email Address *</label>
                                        <input
                                            type="email"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            className="w-full bg-white/5 border border-white/10 rounded-xl p-3 text-white focus:border-premium-gold/50 outline-none"
                                            placeholder="john.doe@example.com"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-white/70 mb-2">Country / Region</label>
                                        <input
                                            type="text"
                                            value={country}
                                            onChange={(e) => setCountry(e.target.value)}
                                            className="w-full bg-white/5 border border-white/10 rounded-xl p-3 text-white focus:border-premium-gold/50 outline-none"
                                            placeholder="Global"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-white/70 mb-2">City</label>
                                        <input
                                            type="text"
                                            value={city}
                                            onChange={(e) => setCity(e.target.value)}
                                            className="w-full bg-white/5 border border-white/10 rounded-xl p-3 text-white focus:border-premium-gold/50 outline-none"
                                            placeholder="City"
                                        />
                                    </div>
                                </div>

                                {!isNoCvRequired && (
                                    <div className="border-t border-white/10 pt-4">
                                        <h3 className="text-lg font-bold text-white mb-4">Professional Details (Optional)</h3>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-white/70 mb-2">Education / Highest Qualification</label>
                                                <input
                                                    type="text"
                                                    value={education}
                                                    onChange={(e) => setEducation(e.target.value)}
                                                    className="w-full bg-white/5 border border-white/10 rounded-xl p-3 text-white focus:border-premium-gold/50 outline-none"
                                                    placeholder="e.g. Self-taught, BSc, High School"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-white/70 mb-2">Years of Experience</label>
                                                <input
                                                    type="text"
                                                    value={experience}
                                                    onChange={(e) => setExperience(e.target.value)}
                                                    className="w-full bg-white/5 border border-white/10 rounded-xl p-3 text-white focus:border-premium-gold/50 outline-none"
                                                    placeholder="e.g. 2 years, Beginner"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-white/70 mb-2">Portfolio / Personal Website</label>
                                                <input
                                                    type="text"
                                                    value={portfolio}
                                                    onChange={(e) => setPortfolio(e.target.value)}
                                                    className="w-full bg-white/5 border border-white/10 rounded-xl p-3 text-white focus:border-premium-gold/50 outline-none"
                                                    placeholder="https://myportfolio.com"
                                                />
                                            </div>
                                            <div className="md:col-span-2">
                                                <label className="block text-white/70 mb-2">Desired Rate / Salary Expectation</label>
                                                <input
                                                    type="text"
                                                    value={rate}
                                                    onChange={(e) => setRate(e.target.value)}
                                                    className="w-full bg-white/5 border border-white/10 rounded-xl p-3 text-white focus:border-premium-gold/50 outline-none"
                                                    placeholder="e.g. $20/hr, $500 fixed"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div>
                                    <label className="block text-white/70 mb-2">Why are you a good fit? *</label>
                                    <textarea
                                        value={applicationMessage}
                                        onChange={(e) => setApplicationMessage(e.target.value)}
                                        className="w-full h-40 bg-white/5 border border-white/10 rounded-xl p-4 text-white focus:border-premium-gold/50 outline-none resize-none"
                                        placeholder="Describe your skills, approach, and why you're interested..."
                                    />
                                </div>

                                {!isNoCvRequired && (
                                    <div>
                                        <label className="block text-white/70 mb-2">CV / Resume URL (Optional)</label>
                                        <input
                                            type="text"
                                            value={cvUrl}
                                            onChange={(e) => setCvUrl(e.target.value)}
                                            className="w-full bg-white/5 border border-white/10 rounded-xl p-4 text-white focus:border-premium-gold/50 outline-none"
                                            placeholder="Dropbox / Google Drive Link"
                                        />
                                    </div>
                                )}

                                <div className="flex gap-4 pt-4">
                                    <button
                                        onClick={handleApply}
                                        disabled={submittingApp || !firstName || !lastName || !age || !email || !applicationMessage}
                                        className="flex-1 py-3 bg-premium-gold text-premium-wood-dark font-bold rounded-xl hover:bg-premium-gold-light transition-colors disabled:opacity-50"
                                    >
                                        {submittingApp ? 'Submitting...' : 'Submit Application'}
                                    </button>
                                    <button
                                        onClick={() => setView('details')}
                                        className="px-6 py-3 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        ) : view === 'applications' ? (
                            <div className="space-y-6">
                                <div className="flex items-center justify-between mb-4">
                                    <h2 className="text-2xl font-serif font-bold text-premium-gold">Applications ({applications.length})</h2>
                                    <button
                                        onClick={() => setView('details')}
                                        className="text-sm text-premium-gold hover:underline"
                                    >
                                        Back to Details
                                    </button>
                                </div>
                                {applications.length === 0 ? (
                                    <p className="text-white/40">No applications yet.</p>
                                ) : (
                                    applications.map(app => (
                                        <div key={app._id} className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-3">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-full bg-premium-gold/20 flex items-center justify-center text-premium-gold font-bold">
                                                    {app.applicant?.firstName?.[0]}{app.applicant?.lastName?.[0]}
                                                </div>
                                                <div>
                                                    <div className="font-semibold text-white">
                                                        {app.applicant?.firstName} {app.applicant?.lastName}
                                                    </div>
                                                    <div className="text-xs text-white/40">Applied {new Date(app.appliedAt).toLocaleDateString()}</div>
                                                </div>
                                                <div className="ml-auto">
                                                    <span className={`px-2 py-1 rounded text-xs font-semibold ${app.status === 'accepted' ? 'bg-green-500/20 text-green-300' :
                                                        app.status === 'rejected' ? 'bg-red-500/20 text-red-300' :
                                                            'bg-yellow-500/20 text-yellow-300'
                                                        }`}>
                                                        {app.status.toUpperCase()}
                                                    </span>
                                                </div>
                                            </div>
                                            <p className="text-white/80 text-sm whitespace-pre-wrap">{app.message}</p>
                                            {app.cvUrl && (
                                                <a href={app.cvUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-premium-gold text-sm hover:underline">
                                                    <BsFileEarmarkText /> View CV
                                                </a>
                                            )}
                                            {app.status === 'pending' && (
                                                <div className="flex gap-2 pt-2">
                                                    <button
                                                        onClick={() => handleAdjudicate(app._id, 'accepted')}
                                                        className="px-3 py-1.5 bg-green-500/20 text-green-300 border border-green-500/30 rounded-lg text-sm hover:bg-green-500/30"
                                                    >
                                                        Accept
                                                    </button>
                                                    <button
                                                        onClick={() => handleAdjudicate(app._id, 'rejected')}
                                                        className="px-3 py-1.5 bg-red-500/20 text-red-300 border border-red-500/30 rounded-lg text-sm hover:bg-red-500/30"
                                                    >
                                                        Reject
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    ))
                                )}
                            </div>
                        ) : (
                            <div className="space-y-6">
                                {/* Header */}
                                <div>
                                    <div className="flex items-center gap-3 mb-2">
                                        {getStatusBadge(task.status)}
                                        <span className="text-white/40 text-sm">Posted {new Date(task.createdAt).toLocaleDateString()}</span>
                                    </div>
                                    <h2 className="text-3xl md:text-4xl font-serif font-bold text-premium-gold leading-tight mb-4">
                                        {task.title}
                                    </h2>

                                    <div className="flex flex-wrap items-center gap-6 text-white/70">
                                        {task.postedBy && (
                                            <div className="flex items-center gap-2">
                                                <div className="w-8 h-8 rounded-full bg-premium-gold/20 flex items-center justify-center text-premium-gold border border-premium-gold/30">
                                                    <span className="font-bold text-xs">
                                                        {task.postedBy.firstName?.[0]}{task.postedBy.lastName?.[0]}
                                                    </span>
                                                </div>
                                                <span className="text-sm font-medium text-white">
                                                    {task.postedBy.firstName} {task.postedBy.lastName}
                                                </span>
                                            </div>
                                        )}
                                        {task.budgetRange && (
                                            <div className="flex items-center gap-2">
                                                <BsCurrencyDollar className="w-5 h-5 text-premium-gold" />
                                                <span>
                                                    {task.budgetRange.min && task.budgetRange.max
                                                        ? `${formatPrice(task.budgetRange.min)} - ${formatPrice(task.budgetRange.max)}`
                                                        : task.budgetRange.min ? `${t('dispatch.budget_from')} ${formatPrice(task.budgetRange.min)}` : `${t('dispatch.budget_up_to')} ${formatPrice(task.budgetRange.max || 0)}`}
                                                </span>
                                            </div>
                                        )}

                                        {/* Expiration HUD */}
                                        {task.expiresAt && (
                                            <div className="flex items-center gap-2 px-3 py-1 rounded bg-orange-500/10 text-orange-400 border border-orange-500/20">
                                                <BsClock className="w-4 h-4" />
                                                <span className="text-xs font-bold uppercase tracking-wider">{t('dispatch.expires_in')}: {timeLeft}</span>
                                            </div>
                                        )}
                                        
                                        {/* Urgency Indicators */}
                                        {task.urgency === 'immediate' && (
                                            <div className="flex items-center gap-2 px-3 py-1 rounded bg-red-500/20 text-red-400 border border-red-500/30">
                                                <FiAlertCircle className="w-4 h-4" />
                                                <span className="text-xs font-bold uppercase tracking-wider">{t('dispatch.urgent')}</span>
                                            </div>
                                        )}

                                        {task.activeViewers && task.activeViewers.length > 0 && (
                                            <div className="flex items-center gap-2 px-3 py-1 rounded bg-blue-500/10 text-blue-400 border border-blue-500/20">
                                                <BsEye className="w-4 h-4" />
                                                <span className="text-xs font-bold uppercase tracking-wider">{task.activeViewers.length} {t('dispatch.viewing_now')}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Description */}
                                <div className="prose prose-invert max-w-none">
                                    <h3 className="text-xl font-bold text-white mb-2">Description</h3>
                                    <p className="text-white/80 whitespace-pre-wrap leading-relaxed">
                                        {task.description}
                                    </p>
                                </div>

                                {/* Job Details Grid */}
                                <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
                                    <h3 className="text-xl font-bold text-white mb-4">Job Details</h3>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                                        {/* Category */}
                                        <div>
                                            <p className="text-white/40 text-xs uppercase font-bold tracking-wider mb-1">Category</p>
                                            <p className="text-white font-medium capitalize">
                                                {task.category || 'Professional'}
                                            </p>
                                        </div>

                                        {/* Work Mode */}
                                        <div>
                                            <p className="text-white/40 text-xs uppercase font-bold tracking-wider mb-1">Work Mode</p>
                                            <p className="text-white font-medium capitalize">
                                                {task.mode === 'remote' ? 'Remote' : task.mode === 'on-site' ? 'On-Site' : 'Hybrid'}
                                            </p>
                                        </div>

                                        {/* Urgency */}
                                        <div>
                                            <p className="text-white/40 text-xs uppercase font-bold tracking-wider mb-1">Urgency</p>
                                            <div className={`inline-flex items-center gap-2 ${task.urgency === 'immediate' ? 'text-red-400' : 'text-white'}`}>
                                                <span className="font-medium capitalize">{task.urgency || 'Standard'}</span>
                                                {task.urgency === 'immediate' && (
                                                    <span className="px-1.5 py-0.5 bg-red-500/20 text-[10px] rounded uppercase font-bold border border-red-500/30">High</span>
                                                )}
                                            </div>
                                        </div>

                                        {/* Timeline */}
                                        <div>
                                            <p className="text-white/40 text-xs uppercase font-bold tracking-wider mb-1">Timeline / Duration</p>
                                            <p className="text-white font-medium">
                                                {task.timeline || 'Flexible'}
                                            </p>
                                        </div>

                                        {/* Pay Structure */}
                                        <div>
                                            <p className="text-white/40 text-xs uppercase font-bold tracking-wider mb-1">Pay Structure</p>
                                            <p className="text-white font-medium capitalize">
                                                {task.payStructure === 'hourly' ? 'Hourly Rate' :
                                                    task.payStructure === 'fixed' ? 'Fixed Price' :
                                                        task.payStructure === 'daily' ? 'Daily Rate' : 'Task Based'}
                                            </p>
                                        </div>

                                        {/* Location - Only if relevant */}
                                        {task.location && (task.location.city || task.location.country) && (
                                            <div>
                                                <p className="text-white/40 text-xs uppercase font-bold tracking-wider mb-1">Location</p>
                                                <p className="text-white font-medium">
                                                    {[task.location.city, task.location.country].filter(Boolean).join(', ')}
                                                </p>
                                            </div>
                                        )}

                                        {/* Tools Required */}
                                        <div>
                                            <p className="text-white/40 text-xs uppercase font-bold tracking-wider mb-1">Tools Required</p>
                                            <p className="text-white font-medium">
                                                {task.toolsRequired ? 'Yes, Bring your own' : 'No, Tools provided'}
                                            </p>
                                        </div>

                                        {/* Physical Effort */}
                                        {task.physicalEffort && (
                                            <div>
                                                <p className="text-white/40 text-xs uppercase font-bold tracking-wider mb-1">Physical Effort</p>
                                                <p className="text-white font-medium capitalize">
                                                    {task.physicalEffort}
                                                </p>
                                            </div>
                                        )}

                                        {/* Language Requirements */}
                                        {task.languageRequirements && task.languageRequirements.length > 0 && (
                                            <div>
                                                <p className="text-white/40 text-xs uppercase font-bold tracking-wider mb-1">Languages</p>
                                                <p className="text-white font-medium">
                                                    {task.languageRequirements.join(', ')}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Skills */}
                                <div>
                                    <h3 className="text-xl font-bold text-white mb-3">Required Skills</h3>
                                    <div className="flex flex-wrap gap-2">
                                        {task.requiredSkills.map((skill: any) => (
                                            <span
                                                key={skill._id || skill}
                                                className="px-3 py-1.5 bg-premium-gold/10 text-premium-gold border border-premium-gold/20 rounded-lg text-sm font-medium"
                                            >
                                                {skill.name ? skill.name : 'Skill'}
                                            </span>
                                        ))}
                                    </div>
                                </div>

                                {/* Action Buttons */}
                                <div className="pt-6 border-t border-premium-gold/20 flex flex-wrap gap-4">
                                    {!isOwner && (
                                        <button
                                            onClick={() => setView('apply')}
                                            className="flex-1 min-w-[140px] px-6 py-3 bg-gradient-to-r from-premium-gold-dark to-premium-gold-light text-premium-wood-dark font-bold rounded-xl hover:shadow-[0_0_20px_rgba(212,175,55,0.5)] transition-all transform hover:scale-105 active:scale-95 flex items-center justify-center gap-2"
                                        >
                                            <BsCheckCircle className="w-5 h-5" />
                                            Apply Now
                                        </button>
                                    )}

                                    {isOwner && (
                                        <>
                                            <button
                                                onClick={() => setView('applications')}
                                                className="px-6 py-3 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-xl transition-all flex items-center justify-center gap-2"
                                            >
                                                <BsFileEarmarkText className="w-5 h-5" />
                                                Applications
                                                {applications.length > 0 && (
                                                    <span className="bg-premium-gold text-premium-wood-dark px-1.5 py-0.5 rounded-full text-xs font-bold ml-1">{applications.length}</span>
                                                )}
                                            </button>
                                            {onStatusChange && (
                                                <button
                                                    onClick={() => onStatusChange(task._id, 'expired')}
                                                    className="px-6 py-3 bg-white/5 hover:bg-white/10 text-white font-semibold rounded-xl transition-all flex items-center justify-center gap-2 border border-white/10"
                                                >
                                                    <BsArchive className="w-5 h-5" />
                                                    Mark Taken
                                                </button>
                                            )}
                                            <button
                                                onClick={handleToggleCommenting}
                                                className="px-6 py-3 bg-white/5 hover:bg-white/10 text-white font-semibold rounded-xl transition-all flex items-center justify-center gap-2 border border-white/10"
                                            >
                                                <BsChat className="w-5 h-5" />
                                                {task.commentingEnabled !== false ? 'Disable Comments' : 'Enable Comments'}
                                            </button>
                                            {onDelete && (
                                                <button
                                                    onClick={() => onDelete(task._id)}
                                                    className="px-6 py-3 bg-red-500/10 hover:bg-red-500/20 text-red-400 font-semibold rounded-xl transition-all flex items-center justify-center gap-2 border border-red-500/20"
                                                >
                                                    <BsTrash className="w-5 h-5" />
                                                    Delete
                                                </button>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Right Panel: Comments & Activity */}
                    {view !== 'apply' && (
                        <div className="w-full md:w-2/5 flex flex-col bg-black/20 transition-all duration-300">
                            <div className="p-4 md:p-6 border-b border-premium-gold/20 bg-black/40">
                                <h3 className="text-xl font-bold text-white">Discussion</h3>
                                {task.commentingEnabled === false && (
                                    <p className="text-xs text-red-400 mt-1">Comments are disabled</p>
                                )}
                            </div>

                            {/* Comments List */}
                            <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4 custom-scrollbar">
                                {!task.comments || task.comments.length === 0 ? (
                                    <div className="text-center py-10 text-white/40">
                                        <p>No comments yet. Be the first to ask specific questions!</p>
                                    </div>
                                ) : (
                                    task.comments.map((comment) => (
                                        <div key={comment._id} className="flex gap-3">
                                            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-premium-gold/10 flex items-center justify-center text-premium-gold border border-premium-gold/20 text-xs font-bold mt-1">
                                                {comment.user?.firstName?.[0] || '?'}{comment.user?.lastName?.[0] || '?'}
                                            </div>
                                            <div className="flex-1">
                                                <div className="bg-white/5 rounded-2xl rounded-tl-none p-3 border border-white/10">
                                                    <div className="flex justify-between items-baseline mb-1">
                                                        <span className="text-sm font-semibold text-white/90">
                                                            {comment.user ? `${comment.user.firstName} ${comment.user.lastName}` : 'Unknown User'}
                                                        </span>
                                                        <span className="text-xs text-white/40">
                                                            {new Date(comment.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                        </span>
                                                    </div>
                                                    <p className="text-white/80 text-sm whitespace-pre-wrap">{comment.text}</p>

                                                    {/* Action Bar */}
                                                    <div className="flex items-center gap-4 mt-2 text-xs text-white/50">
                                                        <button
                                                            onClick={() => handleLike(comment._id)}
                                                            className="hover:text-premium-gold flex items-center gap-1 transition-colors"
                                                        >
                                                            <BsHandThumbsUp /> {comment.likes?.length || 0}
                                                        </button>
                                                        <button
                                                            onClick={() => setReplyingTo(replyingTo === comment._id ? null : comment._id)}
                                                            className="hover:text-premium-gold flex items-center gap-1 transition-colors"
                                                        >
                                                            <BsChat /> Reply
                                                        </button>
                                                        {(isOwner || false/* check if currentUser is author */) && (
                                                            <button
                                                                onClick={() => handleDeleteComment(comment._id)}
                                                                className="hover:text-red-400 transition-colors"
                                                            >
                                                                Delete
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Replies */}
                                                {comment.replies && comment.replies.map(reply => (
                                                    <div key={reply._id} className="flex gap-3 mt-3 ml-2">
                                                        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-white/10 flex items-center justify-center text-white/70 text-[10px] font-bold mt-1">
                                                            {reply.user?.firstName?.[0] || '?'}
                                                        </div>
                                                        <div className="flex-1 bg-white/5 rounded-xl p-2 border border-white/5">
                                                            <div className="flex justify-between items-baseline mb-1">
                                                                <span className="text-xs font-semibold text-white/90">
                                                                    {reply.user?.firstName}
                                                                </span>
                                                            </div>
                                                            <p className="text-white/70 text-xs">{reply.text}</p>
                                                        </div>
                                                    </div>
                                                ))}

                                                {/* Reply Input */}
                                                {replyingTo === comment._id && (
                                                    <div className="mt-3 flex gap-2">
                                                        <input
                                                            type="text"
                                                            value={replyText}
                                                            onChange={(e) => setReplyText(e.target.value)}
                                                            onKeyDown={(e) => e.key === 'Enter' && handleReply(comment._id)}
                                                            placeholder="Write a reply..."
                                                            className="flex-1 bg-black/20 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-premium-gold/50 outline-none"
                                                            autoFocus
                                                        />
                                                        <button
                                                            onClick={() => handleReply(comment._id)}
                                                            className="p-2 bg-premium-gold/20 text-premium-gold rounded-lg hover:bg-premium-gold/30"
                                                        >
                                                            <BsSend className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))
                                )}
                                <div ref={commentsEndRef} />
                            </div>

                            {/* Input Area */}
                            {task.commentingEnabled !== false && (
                                <div className="p-4 md:p-6 border-t border-premium-gold/20 bg-black/40">
                                    {/* Quick Emojis */}
                                    <div className="flex gap-2 mb-3 overflow-x-auto pb-2 scrollbar-hide">
                                        {quickEmojis.map(emoji => (
                                            <button
                                                key={emoji}
                                                onClick={() => handleEmojiClick(emoji)}
                                                className="px-2 py-1 bg-white/5 hover:bg-white/10 rounded text-lg transition-colors"
                                            >
                                                {emoji}
                                            </button>
                                        ))}
                                    </div>

                                    <div className="flex gap-2">
                                        <div className="relative flex-1">
                                            <input
                                                type="text"
                                                value={commentText}
                                                onChange={(e) => setCommentText(e.target.value)}
                                                onKeyDown={(e) => e.key === 'Enter' && handleSendComment()}
                                                placeholder="Ask a question..."
                                                className="w-full bg-white/5 border border-white/10 rounded-xl pl-4 pr-10 py-3 text-white placeholder-white/40 focus:outline-none focus:border-premium-gold/50 transition-colors"
                                            />
                                            <button
                                                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                                                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-premium-gold transition-colors"
                                            >
                                                <BsEmojiSmile className="w-5 h-5" />
                                            </button>
                                        </div>
                                        <button
                                            onClick={handleSendComment}
                                            disabled={!commentText.trim() || submittingComment}
                                            className="p-3 bg-premium-gold text-premium-wood-dark rounded-xl hover:bg-premium-gold-light disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                        >
                                            <BsSend className="w-5 h-5" />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </motion.div>
            </div >
        </AnimatePresence >
    );
};

export default TaskDetailModal;
